<template>
  <v-card flat>
    <v-card-text class="pb-0">
      <v-container grid-list-md fluid class="ma-0 pa-0">
        <v-layout row wrap>
          <!-- Provider Group-->
          <v-flex md3 sm3 v-if="IS_POLARIS">
            <v-autocomplete v-model="selectedProviderGroup" :label="messages.label.PROVIDER_GROUP"
              placeholder="Select Provider Group" item-text="title" item-value="title" clearable open-on-clear
              :loading="loadingProviderGroups" :items="providerGroups" return-object>
              <template v-slot:no-data>
                <v-list-tile>
                  <v-list-tile-title>
                    No Provider groups in
                    <strong>{{ mpxParentAccount.title }}</strong>
                  </v-list-tile-title>
                </v-list-tile>
              </template>
            </v-autocomplete>
          </v-flex>
          <!-- Sub Accounts -->
          <template v-else>
            <v-flex md3 sm3 v-if="mpxSubAccounts.length > 0">
              <v-autocomplete v-model="selectedSubAccount" :label="messages.label.SUB_ACCOUNT" item-text="title"
                item-value="title" :loading="loadingSubAccounts" :items="mpxSubAccounts" return-object>
              </v-autocomplete>
            </v-flex>
            <!-- Sub Account Title-->
            <v-flex md3 sm3 v-else>
              <v-text-field v-model="providerName" clearable :label="messages.label.PROVIDER_NAME" required>
              </v-text-field>
            </v-flex>
          </template>
          <!-- Rule Grouping -->
          <v-flex md2 sm2>
            <v-select v-model="selectedRuleGrouping" :label="messages.label.RULE_GROUPING" :items="ruleGroupingList"
              item-text="text" item-value="value" return-object></v-select>
          </v-flex>
          <!-- Rule Type -->
          <v-flex md2 sm2>
            <v-select v-model="selectedRuleType" :label="messages.label.TYPE" :items="ruleTypes" item-text="text"
              item-value="value" item-disabled="disable" return-object></v-select>
          </v-flex>
          <!-- Rule State -->
          <v-flex md1 sm1>
            <v-select v-model="selectedRuleState" :label="messages.label.RULE_STATE" :items="ruleStateList"
              item-text="text" item-value="value" return-object></v-select>
          </v-flex>
          <!-- Rule Title -->
          <v-flex md2 sm2>
            <v-text-field :label="messages.label.RULE_TITLE" v-model="ruleTitle" @keyup.enter="searchRules()" clearable>
            </v-text-field>
          </v-flex>
          <!-- Search Button -->
          <v-flex md2 sm2>
            <v-btn color="primary" @click="searchRules()">{{
                messages.label.SEARCH
            }}</v-btn>
          </v-flex>
          <v-flex md3 sm3 class="text-xs-right"> </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import messages from "@/MetadataRules/messages.json"
import { IS_POLARIS } from "@/store/constants/getter-types"
import { GET_PROVIDER_GROUPS } from "@/store/constants/action-types"
import AppService from "@/AppService"

/**
 * initialData
 */
function initialData(instance) {
  return {
    selectedRuleGrouping: { text: "Rules Only", value: "rules" },
    ruleGroupingList: instance.getRuleGroupings(),
    ruleTypes: instance.getRuleTypes(),
    ruleStateList: instance.getRuleStates(),
    selectedRuleType: { text: "ALL", value: "" },
    selectedRuleState: { text: "All", value: "all" },
    ruleTitle: "",
    loadingProviderGroups: false,
    selectedProviderGroup: {},
    selectedSubAccount: {},
    providerName: "",
    loadingSubAccounts: false
  }
}

export default {
  name: "MetadataRulesSearchFilter",
  data: function () {
    return initialData(this)
  },
  computed: {
    ...mapState(["mpxParentAccount", "providerGroups", "mpxSubAccounts"]),
    ...mapGetters([IS_POLARIS])
  },
  watch: {
    selectedRuleGrouping: {
      handler(newVal) {
        this.ruleTypes = this.getRuleTypes(newVal)
        if (newVal.value === "ruleSets" || newVal.value === "ruleCollection") {
          if (this.selectedRuleType.value === "TRANSFORMATION_JS") {
            this.selectedRuleType = { text: "ALL", value: "" }
          }
        }
      }
    }
  },
  methods: {
    ...mapActions([GET_PROVIDER_GROUPS]),

    /**
     * reset
     */
    reset() {
      Object.assign(this.$data, initialData(this))
      this.retrieveProviderGroups()
    },

    /**
     * getRuleGroupings
     * @returns {array}
     */
    getRuleGroupings() {
      return [
        { text: "Rules Only", value: "rules" },
        { text: "Rule Sets", value: "ruleSets" },
        { text: "Rule Collection", value: "ruleCollection" }
      ]
    },

    /**
     * getRuleTypes
     * @returns {array}
     */
    getRuleTypes(ruleGrouping) {
      if (!ruleGrouping) {
        ruleGrouping = { text: "Rules Only", value: "rules" }
      }
      let disableTbyJS =
        ruleGrouping.value === "ruleSets" ||
        ruleGrouping.value === "ruleCollection"
      return [
        { text: "ALL", value: "", disable: false },
        { text: "VALIDATION", value: "VALIDATION", disable: false },
        { text: "TRANSFORMATION", value: "TRANSFORMATION", disable: false },
        {
          text: "TRANSFORMATION by JS",
          value: "TRANSFORMATION_JS",
          disable: disableTbyJS
        }
      ]
    },

    /**
     * getRuleStates
     * @returns {array}
     */
    getRuleStates() {
      return [
        { text: "All", value: "all" },
        { text: "Enabled", value: "enabled" },
        { text: "Disabled", value: "disabled" }
      ]
    },
    /**
     * searchRules - trigger the search event for retrieving all the rules, rule sets and rule collection
     */
    searchRules() {
      let searchParams = {
        grouping: this.selectedRuleGrouping.value
      }
      // Disabled query
      if (this.selectedRuleState.value === "enabled") {
        searchParams.byDisabled = false
      } else if (this.selectedRuleState.value === "disabled") {
        searchParams.byDisabled = true
      }
      // Title Query
      if (this.ruleTitle !== "") {
        searchParams.byTitlePrefix = this.ruleTitle
      }
      // Type Query
      if (this.selectedRuleType.value !== "") {
        searchParams.byType = this.selectedRuleType.value
      }
      // Provider Group or Sub Account
      if (this.IS_POLARIS) {
        if (!this.$lodash.isEmpty(this.selectedProviderGroup)) {
          // Provider Group account id will be passed in context
          searchParams.account = this.selectedProviderGroup.ownerId
        } else {
          // Parent account id will be passed in context
          searchParams.account = this.mpxParentAccount.id
        }
      } else {
        if (!this.$lodash.isEmpty(this.selectedSubAccount)) {
          // Sub acoount id will be passed in context
          searchParams.account = this.selectedSubAccount.id
        } else {
          // Parent account id will be passed in context
          searchParams.account = this.mpxParentAccount.id
        }
        // Sub Account Title
        if (
          !this.$lodash.isEmpty(this.providerName) &&
          !this.$lodash.isNull(this.providerName)
        ) {
          searchParams.bySecondaryOwner = this.providerName
        }
      }

      MetadataRulesService.$emit("event:searchMetadataRules", searchParams)
    },

    /**
     * retrieveProviderGroups - retrieve provider groups
     */
    retrieveProviderGroups() {
      // If the account is not Polaris, just return empty array
      if (!this.IS_POLARIS) {
        return new Promise(function (resolve) {
          resolve([])
        })
      }
      this.loadingProviderGroups = true
      this.GET_PROVIDER_GROUPS().finally(() => {
        this.loadingProviderGroups = false
      })
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    //  Retrieve providers list
    this.retrieveProviderGroups()
    /**
     * Events
     */
    AppService.$on("event:mpxAccountChanged", this.reset)
  },
  beforeDestroy() {
    AppService.$off("event:mpxAccountChanged", this.reset)
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/MetadataRules/MetadataRulesSearchFilter.vue